/**
 * Helpers for paginated Read lesson content from API HTML.
 * Semantic blocks + approximate word count; never split paragraphs.
 */

const BLOCK_TAGS = new Set([
    'P',
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6',
    'UL',
    'OL',
    'BLOCKQUOTE',
    'PRE',
    'TABLE',
    'HR',
    'FIGURE',
    'SECTION',
    'ARTICLE',
    'DIV',
]);

const HEADING_TAGS = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6']);

const ALLOWED_TAGS = new Set([
    'P',
    'BR',
    'STRONG',
    'B',
    'EM',
    'I',
    'U',
    'A',
    'UL',
    'OL',
    'LI',
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6',
    'BLOCKQUOTE',
    'SPAN',
    'DIV',
    'HR',
    'FIGURE',
    'FIGCAPTION',
]);

const ALLOWED_ATTRS = {
    A: new Set(['href', 'title', 'target', 'rel']),
    SPAN: new Set(['class']),
    DIV: new Set(['class']),
    P: new Set(['class']),
};

const TARGET_MIN = 350;
const TARGET_MAX = 500;
const HEADING_BREAK_AFTER = 200;

function decodeEntities(text) {
    if (!text) return '';
    const el = document.createElement('textarea');
    el.innerHTML = text;
    return el.value;
}

function countWords(text) {
    const cleaned = String(text || '')
        .replace(/\s+/g, ' ')
        .trim();
    if (!cleaned) return 0;
    return cleaned.split(' ').filter(Boolean).length;
}

function isStrongOnlyParagraph(el) {
    if (!el || el.tagName !== 'P') return false;
    const clone = el.cloneNode(true);
    clone.querySelectorAll('br').forEach((br) => br.remove());
    const kids = Array.from(clone.childNodes).filter((n) => {
        if (n.nodeType === Node.TEXT_NODE) return n.textContent.trim().length > 0;
        if (n.nodeType === Node.ELEMENT_NODE) return true;
        return false;
    });
    if (kids.length !== 1 || kids[0].nodeType !== Node.ELEMENT_NODE) return false;
    const only = kids[0];
    return only.tagName === 'STRONG' || only.tagName === 'B';
}

function isHeadingBlock(el) {
    if (!el) return false;
    if (HEADING_TAGS.has(el.tagName)) return true;
    return isStrongOnlyParagraph(el);
}

/** Strip unsafe tags/attrs; keep allowed markup. */
export function sanitizeLessonHtml(html) {
    if (!html || typeof html !== 'string') return '';
    const normalized = String(html)
        .replace(/className=/g, 'class=')
        .replace(/<\s*script[\s\S]*?>[\s\S]*?<\s*\/\s*script\s*>/gi, '')
        .replace(/<\s*iframe[\s\S]*?>[\s\S]*?<\s*\/\s*iframe\s*>/gi, '')
        .replace(/<\s*object[\s\S]*?>[\s\S]*?<\s*\/\s*object\s*>/gi, '')
        .replace(/<\s*embed[\s\S]*?>/gi, '');

    const wrapper = document.createElement('div');
    wrapper.innerHTML = normalized;

    const walk = (node) => {
        const children = Array.from(node.childNodes);
        children.forEach((child) => {
            if (child.nodeType === Node.ELEMENT_NODE) {
                const tag = child.tagName;
                if (!ALLOWED_TAGS.has(tag)) {
                    // unwrap: keep children
                    while (child.firstChild) {
                        node.insertBefore(child.firstChild, child);
                    }
                    node.removeChild(child);
                    return;
                }
                // strip event handlers / disallowed attrs
                Array.from(child.attributes).forEach((attr) => {
                    const name = attr.name.toLowerCase();
                    if (name.startsWith('on') || name === 'style') {
                        child.removeAttribute(attr.name);
                        return;
                    }
                    const allowed = ALLOWED_ATTRS[tag];
                    if (allowed && !allowed.has(attr.name)) {
                        child.removeAttribute(attr.name);
                    }
                });
                if (tag === 'A') {
                    const href = child.getAttribute('href') || '';
                    if (/^\s*javascript:/i.test(href)) {
                        child.removeAttribute('href');
                    }
                    if (child.getAttribute('target') === '_blank') {
                        child.setAttribute('rel', 'noopener noreferrer');
                    }
                }
                walk(child);
            }
        });
    };

    walk(wrapper);
    return wrapper.innerHTML;
}

/**
 * Parse HTML string into semantic top-level blocks.
 * @returns {{ html: string, text: string, words: number, isHeading: boolean, type: string }[]}
 */
export function parseHtmlBlocks(html) {
    const safe = sanitizeLessonHtml(html);
    if (!safe.trim()) return [];

    const wrapper = document.createElement('div');
    wrapper.innerHTML = safe;

    const blocks = [];

    const pushEl = (el) => {
        const text = decodeEntities(el.textContent || '');
        const words = countWords(text);
        if (!words && el.tagName !== 'HR' && !el.querySelector('img')) return;
        const heading = isHeadingBlock(el);
        let htmlOut = el.outerHTML;
        if (heading && el.tagName === 'P') {
            // mark for styling as section heading
            htmlOut = `<p class="dv2-read__heading">${el.innerHTML}</p>`;
        } else if (heading && HEADING_TAGS.has(el.tagName)) {
            htmlOut = el.outerHTML.replace(
                /^<h([1-6])/i,
                '<h$1 class="dv2-read__heading"'
            );
        }
        blocks.push({
            type: 'html',
            html: htmlOut,
            text,
            words,
            isHeading: heading,
        });
    };

    Array.from(wrapper.childNodes).forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent.trim();
            if (!text) return;
            const words = countWords(text);
            blocks.push({
                type: 'html',
                html: `<p>${text}</p>`,
                text,
                words,
                isHeading: false,
            });
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) return;

        if (BLOCK_TAGS.has(node.tagName)) {
            // flatten nested divs that only wrap blocks
            if (
                node.tagName === 'DIV' &&
                !node.querySelector('img') &&
                Array.from(node.children).every((c) => BLOCK_TAGS.has(c.tagName))
            ) {
                Array.from(node.children).forEach(pushEl);
            } else {
                pushEl(node);
            }
        } else {
            // inline leftover → wrap
            const text = (node.textContent || '').trim();
            if (!text) return;
            blocks.push({
                type: 'html',
                html: `<p>${node.outerHTML}</p>`,
                text,
                words: countWords(text),
                isHeading: false,
            });
        }
    });

    return blocks;
}

/**
 * Build ordered blocks: intro → image → content.
 */
export function buildReadBlocks({ introText, contentText, imageUrl }) {
    const blocks = [];
    parseHtmlBlocks(introText || '').forEach((b) => blocks.push(b));

    if (imageUrl) {
        blocks.push({
            type: 'image',
            src: imageUrl,
            html: '',
            text: '',
            words: 0,
            isHeading: false,
        });
    }

    parseHtmlBlocks(contentText || '').forEach((b) => blocks.push(b));
    return blocks;
}

/**
 * Paginate blocks by approximate word count (350–500), never splitting blocks.
 * Prefer page break before headings when current page already has enough content.
 */
export function paginateReadBlocks(blocks, options = {}) {
    const minWords = options.minWords ?? TARGET_MIN;
    const maxWords = options.maxWords ?? TARGET_MAX;
    const headingBreakAfter = options.headingBreakAfter ?? HEADING_BREAK_AFTER;

    if (!blocks.length) {
        return [[]];
    }

    const pages = [];
    let current = [];
    let words = 0;

    const flush = () => {
        if (current.length) {
            pages.push(current);
            current = [];
            words = 0;
        }
    };

    blocks.forEach((block) => {
        const wouldExceed = words + block.words > maxWords && current.length > 0;
        const preferBreakForHeading =
            block.isHeading &&
            words >= headingBreakAfter &&
            current.length > 0;

        if (
            (wouldExceed || preferBreakForHeading) &&
            !(block.type === 'image' && words < minWords && current.length > 0)
        ) {
            // Don't leave a nearly empty page if we're under min and adding image
            if (wouldExceed || preferBreakForHeading) {
                flush();
            }
        }

        // If a single block is huge, it still gets its own page(s) as one block
        current.push(block);
        words += block.words;

        if (words >= maxWords) {
            flush();
        }
    });

    flush();
    return pages.length ? pages : [[]];
}

const MOBILE_MIN = 80;
const MOBILE_MAX = 170;
const MOBILE_HEADING_BREAK = 60;
const MOBILE_MAX_BODY = 2;

function splitPagesByBodyCap(pages, maxBody = MOBILE_MAX_BODY) {
    const out = [];
    pages.forEach((page) => {
        let current = [];
        let bodyCount = 0;
        page.forEach((block) => {
            const isBody = block.type === 'html' && !block.isHeading;
            if (isBody && bodyCount >= maxBody && current.length) {
                out.push(current);
                current = [];
                bodyCount = 0;
            }
            current.push(block);
            if (isBody) bodyCount += 1;
        });
        if (current.length) out.push(current);
    });
    return out.length ? out : [[]];
}

/**
 * Mobile reading slices: ~120–180 words, 1–2 body paragraphs,
 * optional hero image on the first slice. Never splits a paragraph.
 */
function mergeHeadingOnlySlices(slices) {
    const out = [];
    slices.forEach((slice) => {
        const hasBody = slice.some((b) => b.type === 'html' && !b.isHeading);
        if (!hasBody && out.length) {
            out[out.length - 1] = out[out.length - 1].concat(slice);
            return;
        }
        if (
            out.length &&
            !out[out.length - 1].some((b) => b.type === 'html' && !b.isHeading)
        ) {
            out[out.length - 1] = out[out.length - 1].concat(slice);
            return;
        }
        out.push(slice);
    });
    return out.length ? out : [[]];
}

export function paginateReadSlicesMobile(blocks) {
    const image = (blocks || []).find((b) => b.type === 'image') || null;
    const htmlBlocks = (blocks || []).filter((b) => b.type !== 'image');
    const wordPages = paginateReadBlocks(htmlBlocks, {
        minWords: MOBILE_MIN,
        maxWords: MOBILE_MAX,
        headingBreakAfter: MOBILE_HEADING_BREAK,
    });
    const slices = mergeHeadingOnlySlices(
        splitPagesByBodyCap(wordPages, MOBILE_MAX_BODY)
    );
    if (image && slices[0]) {
        slices[0] = [image, ...slices[0]];
    }
    return slices.length ? slices : [[]];
}

export function renderPageHtml(pageBlocks) {
    return (pageBlocks || [])
        .filter((b) => b.type === 'html')
        .map((b) => b.html)
        .join('');
}
